/**
 * Espaçamento, raio, borda, sombra, alvos e movimento.
 * Fonte: "Arquitetura e Design System v2" e as medidas do protótipo.
 */

/** Escala de espaçamento do design. Use os valores, não números soltos. */
export const space = [3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 16, 18, 20] as const;

export const layout = {
  /** Margem lateral padrão da tela. */
  screenPadding: 16,
  /** Gap entre blocos de conteúdo. */
  blockGap: 12,
  /**
   * Ritmo vertical (decisão do PO, 12/09): toda tela respira o mesmo.
   * `sectionGap` separa uma seção da anterior (o rótulo de seção carrega
   * isso); `labelGap` é o ar entre o rótulo e o primeiro bloco da seção;
   * `headerGap` é o respiro abaixo do cabeçalho da tela.
   */
  sectionGap: 24,
  labelGap: 8,
  headerGap: 16,
  /**
   * Ar acima de todo cabeçalho de tela — o mesmo em todas as abas. A safe
   * area do aparelho (notch, Dynamic Island) NÃO entra aqui: só o shell a
   * aplica, via `--safe-*`, e nenhuma tela precisa saber dela.
   */
  headerTop: 12,
} as const;

/**
 * Raios normalizados (PRD §22). A escala alvo é esta — telas antigas do
 * protótipo ainda usam 16/18/22/24 e devem ser normalizadas ao serem
 * implementadas.
 */
export const radius = {
  hero: 26,
  card: 20,
  control: 14,
  /** Tile pequeno: ícone em caixa (34–40px), zona de soltar. É o quinto valor
   *  da escala, e o único abaixo de 14 — 10, 11 e 16 não existem. */
  tile: 12,
  chip: 999,
} as const;

export const border = {
  hairline: 1,
  /** Destaque na cor do acento: foco de campo, item pego no reorder. */
  emphasis: 1.5,
} as const;

/**
 * Elevação por NÍVEL, não por uso — uma sombra nova não precisa inventar um
 * nome. e0 é o padrão e não tem sombra: card, lista e campo se separam por
 * fio. Sombra difusa é exceção, para o que flutua ou cobre.
 */
export const shadow = {
  /** e0 — card, lista, campo. Sem sombra: separação por fio. */
  e0: null,
  /**
   * raised — o único card que se levanta da página (A2, rodada 3): o AGORA da
   * Home e o saldo do Financeiro. Difusa e leve; o resto é `flat` (fio).
   */
  raised: { offsetY: 8, blur: 24, color: 'rgba(20,20,20,0.08)' },
  /** e1 — flutua: toast, item pego no reorder. */
  e1: { offsetY: 14, blur: 30, color: 'rgba(0,0,0,0.35)' },
  /** e2 — sobre tudo: folha aberta. */
  e2: { offsetY: 22, blur: 60, color: 'rgba(0,0,0,0.24)' },
  /** brand — FAB e CTA laranja: brilho da própria marca, recolhido (spread −4). */
  brand: { offsetY: 8, blur: 20, spread: -4, color: 'rgba(248,72,29,0.55)' },
} as const;

/** Acessibilidade: mínimos que não encolhem em nenhuma faixa de tela. */
export const a11y = {
  /** Alvo de toque mínimo — PRD §23. */
  minTarget: 44,
  /** Menor tamanho aceitável para rótulo de conteúdo. Era 11; PO 11/09. */
  minContentLabelFontSize: 12,
  listRowMin: 46,
  listRowMax: 52,
} as const;

/**
 * Movimento (refino final, 23/09/2026). Transição de interface fica em até
 * 300ms; acima disso só o que é celebração (pódio, selo) ou ambiente
 * (flutuar, brilho do skeleton), e tudo desliga com `prefers-reduced-motion`.
 */
export const motion = {
  pressScale: 0.975,
  /** Card pressionado encolhe menos que botão; FAB, um pouco mais. */
  pressScaleCard: 0.985,
  pressScaleFab: 0.96,
  pressMs: 120,
  screenEnterMs: 220,
  screenEnterTranslateY: 14,
  /** Troca de aba / segmented: indicador desliza, conteúdo entra. */
  tabMs: 220,
  sheetMs: 260,
  scrimMs: 200,
  /** ✓: preenchimento e traço desenhado (stroke-dashoffset 24→0). */
  checkFillMs: 200,
  checkStrokeMs: 280,
  /** Barra de progresso anima a largura. */
  progressMs: 400,
  /** Selo/estado que entra com pop. */
  popMs: 400,
  toastEnterMs: 260,
  /** Quanto o toast fica na tela; com "Desfazer", fica mais. */
  toastMs: 2600,
  toastUndoMs: 5000,
  /** Lista entrando: cada item atrasa isto em relação ao anterior. */
  staggerMs: 30,
  /** Flutuar (avião, mala, coroa): ciclo de ambiente. */
  floatMs: 3200,
  /** Brilho do skeleton. */
  shimmerMs: 1400,
  pulseMs: 1400,
  pulseSlowMs: 2000,
  /** Curva única para toda transição — antes cada uma escolhia a sua. */
  easing: 'cubic-bezier(0.2, 0.7, 0.3, 1)',
  /** Quique do ✓ e do selo: passa do ponto e volta. */
  easingBounce: 'cubic-bezier(0.3, 1.6, 0.5, 1)',
  /** Com `prefers-reduced-motion` tudo cai para isto: o estado muda, só não anima. */
  reducedMs: 0,
} as const;

/**
 * O app é mobile-only (decisão do PO, 11/09). `compact` segue útil: é o piso
 * onde a hora do card AGORA precisa encolher. Tablet e desktop não são alvo.
 */
export const breakpoints = {
  compact: 375,
} as const;
